import { TextField } from '@mui/material';
import React from 'react';
import { FlexRow, MultiSelect, Spacer } from '../../../../common/components/v2';
import ElementLocators from '../../../../common/ElementLocators';
import {
  hasQuote,
  OTHER_APPROVAL_REASON,
  piaPlanFApprovalReasonOptions,
} from '../piaPlanFApprovalReasons';

const REQUIRED_MESSAGE = 'Required field';
const QUOTE_MESSAGE = `Use ' instead of ", or remove it`;

interface ApprovalReasonFieldProps {
  selected: string[];
  setSelected: (selected: string[]) => void;
  otherText: string;
  setOtherText: (text: string) => void;
  /** Show "Required field" under the inputs that are still empty. */
  showRequiredErrors: boolean;
}

/**
 * Reason dropdown for PIA and Plan F approvals. Selecting "Other" reveals a text
 * box for a reason that is not in the list.
 */
export const ApprovalReasonField: React.FC<ApprovalReasonFieldProps> = ({
  selected,
  setSelected,
  otherText,
  setOtherText,
  showRequiredErrors,
}) => {
  const isOtherSelected = selected.includes(OTHER_APPROVAL_REASON);

  const handleSelectedChange = (next: string[]) => {
    setSelected(next);
    if (!next.includes(OTHER_APPROVAL_REASON)) {
      setOtherText('');
    }
  };

  const otherTextError = hasQuote(otherText)
    ? QUOTE_MESSAGE
    : showRequiredErrors && otherText.trim() === ''
      ? REQUIRED_MESSAGE
      : undefined;

  return (
    <>
      <FlexRow>
        <MultiSelect
          required
          fullWidth
          label="Reason"
          options={piaPlanFApprovalReasonOptions}
          value={selected}
          onChange={handleSelectedChange}
          error={
            showRequiredErrors && selected.length === 0
              ? REQUIRED_MESSAGE
              : undefined
          }
          slotProps={{ inputLabel: { shrink: true } }}
        />
      </FlexRow>
      {isOtherSelected && (
        <>
          <Spacer height={12} />
          <FlexRow>
            <TextField
              required
              multiline
              minRows={4}
              slotProps={{ inputLabel: { shrink: true } }}
              name={ElementLocators.LOCATOR_OTAF_REASONS_INPUT}
              data-tid={ElementLocators.LOCATOR_OTAF_REASONS_TEXT_AREA}
              placeholder="Describe the reason"
              size="small"
              label="Other reason"
              value={otherText}
              variant="outlined"
              margin="dense"
              fullWidth
              error={!!otherTextError}
              helperText={otherTextError}
              onChange={event => setOtherText(event.currentTarget.value)}
            />
          </FlexRow>
        </>
      )}
    </>
  );
};
